import { create } from 'zustand';
import { useBackendStore } from '@/store/backendStore';
import { useDeploymentStore } from '@/store/deploymentStore';
import { webDemoListings, fetchWebDemoListings } from '@/demos/webDemos';
import type { AppListing } from '@/backend/types';

interface MyAppsState {
  catalog: AppListing[];
  myApps: AppListing[];
  isLoading: boolean;
  error: string | null;
  loadCatalog: () => Promise<void>;
  loadMyApps: () => Promise<void>;
  getApp: (appId: string) => Promise<AppListing | null>;
  enroll: (appId: string) => Promise<void>;
  isEnrolled: (appId: string) => boolean;
}

export const useMyAppsStore = create<MyAppsState>((set, get) => ({
  catalog: [],
  myApps: [],
  isLoading: false,
  error: null,

  loadCatalog: async () => {
    set({ isLoading: true, error: null });
    try {
      const catalog = await useBackendStore.getState().platform.listApps();
      set({ catalog, isLoading: false });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load apps', isLoading: false });
    }
  },

  loadMyApps: async () => {
    /*
     * The bundled demos go on screen NOW, not when the network comes back.
     *
     * They were built from a local list and then held until the fetch resolved, so the
     * comment below was true of the intent and false of the code: an account with a lot of
     * apps waited seconds on "No apps yet" — the emptiest possible answer — while the
     * six things everyone is given sat in memory the whole time.
     */
    set({ myApps: webDemoListings() });

    // Then the server's list, which is the one allowed to change — a rebuilt demo can be
    // given a new slug, and that must not need an app update to reach anyone.
    const base = useDeploymentStore.getState().config?.apiBaseUrl ?? '';
    const demos = base ? await fetchWebDemoListings(base) : webDemoListings();
    set({ myApps: demos });

    try {
      const enrolled = await useBackendStore.getState().platform.listMyApps();
      const extra = enrolled.filter((a) => !demos.some((d) => d.appId === a.appId));
      set({ myApps: [...demos, ...extra] });
    } catch (e) {
      set({ myApps: demos, error: e instanceof Error ? e.message : 'Failed to load my apps' });
    }
  },

  getApp: (appId) => useBackendStore.getState().platform.getApp(appId),

  enroll: async (appId) => {
    await useBackendStore.getState().platform.enroll(appId);
    await get().loadMyApps();
  },

  isEnrolled: (appId) => get().myApps.some((a) => a.appId === appId),
}));
